import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { EventsByResourceType, EventsListType, EventType, SchedulerDraggableId, SchedulerDroppableId } from "../../SchedulingTypes";
import { saveData } from "services/api/saveData";
import { t } from "i18next";
import { convertUTCToLocalDateTimeString } from "internationalization/timezoneConversions";
import { generateTimeKeyFromDateTimeString, calculateDurationMinutesFromDateTimes } from "../../../../views/scheduling/functions/schedulingDateUtils";

export const handleDragBetweenProjects = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    userLocale: string,
    userTimezone: string,
    setFloatingAlert: Function,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
) => {

    // Get the values from the drag result
    const { source, destination, draggableId } = dragResult;

    if (!destination) return;

    // Get the source type, destination type and draggable type
    const sourceType = source?.droppableId?.type;
    const destinationType = destination.droppableId.type;

    // Handle drag from between project jobs
    if (draggableId.type === 'projectJobEmployeeEvent' && sourceType === 'projectJob' && destinationType === 'projectJob') {

        // Get the source id and destination id
        const sourceResourceId = source?.droppableId?.resourceId;
        const destinationResourceId = destination.droppableId.resourceId;

        if (!sourceResourceId || !destinationResourceId) return;

        // Return if the source is the same as the destination
        if (sourceResourceId === destinationResourceId) return;

        // Get the values of the draggable event
        const { assigneeId, assigneeName, appointmentId, key: eventKey } = draggableId.event;

        // Get the destination values
        const { project_number: new_project_number } = destination.droppableId;

        try {
            const response = await saveData({
                apiUrl: "project_scheduler_move_employee_project_job",
                method: "post",
                data: {
                    appointment_id: appointmentId,
                    job_id: destinationResourceId,
                    user_id: assigneeId
                }
            })

            // Update the event state
            if (response?.status === 200) {

                // Get the data from the response
                const appointmentId = response.data.id;
                const status = response.data.status;

                // Convert datetimes into local times
                const startDateTime = convertUTCToLocalDateTimeString(response.data.start_datetime, userLocale, userTimezone);
                const endDateTime = convertUTCToLocalDateTimeString(response.data.end_datetime, userLocale, userTimezone);

                // Generate event key
                const newKey = `projectJobEmployeeEvent-${appointmentId}-${destinationResourceId}-${assigneeId}`;

                // Create a new event
                const newEvent: EventType = {
                    key: newKey,
                    type: "projectJobEmployeeEvent",
                    appointmentId,
                    assigneeId,
                    resourceId: destinationResourceId,
                    startDateTime,
                    endDateTime,
                    dayKey: draggableId.event.dayKey,
                    startTime: generateTimeKeyFromDateTimeString(startDateTime),
                    endTime: generateTimeKeyFromDateTimeString(endDateTime),
                    durationMinutes: calculateDurationMinutesFromDateTimes(startDateTime, endDateTime),
                    assigneeName,
                    status,
                    deleted: false
                }

                // Remove the old and add the event to the events state
                setEvents(prev => {
                    const updated = { ...prev };
                    delete updated[eventKey];
                    updated[newKey] = newEvent;
                    return updated;
                })

                // Update resource mapping
                setEventsByResource(prev => {
                    const sourceEvents = prev[sourceResourceId] || []
                    const destinationEvents = prev[destinationResourceId] || []

                    return {
                        ...prev,
                        [sourceResourceId]: sourceEvents.filter((key: string) => key !== eventKey),
                        [destinationResourceId]: [...destinationEvents, newKey],
                    }
                })

                // Show floating alert on success
                setFloatingAlert({
                    type: "success",
                    message: t('scheduling.scheduling_board.employee_moved_to_project', { 
                        employee: assigneeName,
                        project_number: new_project_number,
                    })
                });
            }
        } catch {
            setFloatingAlert({
                type: "danger",
                message: t('scheduling.scheduling_board.employee_move_error')
            });
        }
    }
}